'use client';

import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { alpha, Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';

import { Avatar, TraderProfileCardSkeleton } from '@/components';
import { useT } from '@/hooks';
import { formatCompactNumber, formatCurrency } from '@/utils';

interface TraderProfileCardProps {
  address: string;
  name?: string;
  pseudonym?: string;
  joinedDate: string;
  views: string;
  positionsValue: number;
  biggestWin: number;
  predictions: number;
  avatarUrl?: string | null;
  isLoading?: boolean;
}

const StatItem = ({
  label,
  value,
  showDivider = false,
}: {
  label: string;
  value: string | number;
  showDivider?: boolean;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          fontSize: '1.15rem',
          lineHeight: 1.2,
          mb: 0.3,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', display: 'block', fontSize: '0.8rem', fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Box>
    {showDivider && (
      <Box
        sx={{
          width: '1px',
          height: 32,
          bgcolor: (theme) => alpha(theme.palette.divider, 0.6),
          mx: { xs: 2, sm: 3 },
          display: 'block',
        }}
      />
    )}
  </Box>
);

export const TraderProfileCard = ({
  address,
  name,
  pseudonym,
  joinedDate,
  views,
  positionsValue,
  biggestWin,
  predictions,
  avatarUrl,
  isLoading,
}: TraderProfileCardProps) => {
  const [copied, setCopied] = useState(false);
  const { t } = useT();

  const truncateAddress = (addr: string) => {
    if (!addr) return '';
    return `${addr.slice(0, 14)}...${addr.slice(-4)}`;
  };

  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const displayName = name || pseudonym || truncateAddress(address);

  if (isLoading) {
    return <TraderProfileCardSkeleton />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        p: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Avatar src={avatarUrl} alt={address} size={48} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                letterSpacing: '-0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '1.5rem',
              }}
            >
              {displayName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={copied ? 'Copied!' : 'Copy Wallet Address'} placement="top">
                <IconButton
                  size="small"
                  onClick={handleCopyAddress}
                  sx={{ color: 'text.secondary', p: 0.4 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.8rem',
            }}
          >
            {t('leaderboardPortfolio.joined')} {joinedDate} · {views}{' '}
            {t('leaderboardPortfolio.views')}
          </Typography>
        </Box>
      </Box>

      {/* Stats Section */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
        <StatItem
          label={t('leaderboardPortfolio.positionsValue')}
          value={`$${formatCompactNumber(positionsValue ?? 0)}`}
          showDivider
        />
        <StatItem
          label={t('leaderboardPortfolio.biggestWin')}
          value={biggestWin != null ? `$${formatCompactNumber(biggestWin)}` : '-'}
          showDivider
        />
        <StatItem
          label={t('leaderboardPortfolio.predictions')}
          value={(predictions ?? 0).toLocaleString()}
        />
      </Box>
    </Box>
  );
};
